import React, { Fragment, useState } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import TextField from "@material-ui/core/TextField";
import { KeyboardDatePicker } from "@material-ui/pickers";
import * as Yup from "yup";

import MakeAPICall from "../../apiCalls/common";

import {
  editSupportStaffApiRoute,
  addSupportStaffApiRoute
} from "../../apiRoutes";

import {
  phoneRegex,
  MAX_FILE_SIZE,
  IMAGES_FORMATS
} from "../../constants/constants";

import {
  getFormattedDate,
  getFormattedMonthDateV2
} from "../../utils/dateTimeHelper";

import Error from "../../components/error";
import Button from "../../components/button";
import ICInput from "../../components/form/input";
import ICPhoneInput from "../../components/form/phoneInput";
import Loader from "../../components/loader";

import ICTempImg from "../../assets/images/default-picture.png";

const AddLoginForm = props => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiCallParams, setApiCallParams] = useState(null);
  const [serverErrorMsg, setServerErrorMsg] = useState("");
  const [fileError, setFileError] = useState("");

  // validate input file
  const validateFile = file => {
    setFileError("");
    if (file) {
      if (file.size >= MAX_FILE_SIZE) {
        setFileError("Maximum 2MB file is allowed");
        return false;
      } else if (!IMAGES_FORMATS.includes(file.type)) {
        setFileError(
          "File must be one of these formats only jpg, jpeg, png, svg"
        );
        return false;
      }
    }
    return true;
  };

  const callStaffApi = values => {
    var formData = new FormData();
    if (values.middleName) {
      formData.append("middleName", values.middleName);
    }
    formData.append("firstName", values.firstName);
    formData.append("lastName", values.lastName);
    formData.append("email", values.email);
    formData.append("phoneNumber", values.phoneNumber);
    formData.append("dob", getFormattedDate(values.dob));
    formData.append("gender", values.gender);

    if (values.profilePic !== null) {
      formData.append("profilePic", values.profilePic);
    }

    formData.append("userType", values.userType);

    // check whether to call add or edit api
    let apiCallType = {
      type: "POST",
      url: addSupportStaffApiRoute
    };

    if (!props.isEditProfile) {
      formData.append("pin", "0000");
    } else {
      apiCallType = {
        type: "PUT",
        url: editSupportStaffApiRoute + props.userData._id
      };
    }

    setIsProcessing(true);
    setApiCallParams({
      reqType: apiCallType.type,
      reqUrl: apiCallType.url,
      dataObj: formData,
      successCallback: async res => {
        setIsProcessing(false);
        formik.resetForm();
        if (!props.isEditProfile) {
          if (props.LoginAddedSuccess) {
            props.LoginAddedSuccess();
          }
        } else {
          if (props.LoginEditSuccess) {
            props.LoginEditSuccess();
          }
        }
      },
      failureCallback: err => {
        setIsProcessing(false);
        //setServerErrorMsg(err.message);
      }
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: props.userData ? props.userData.firstName : "",
      middleName: props.userData ? props.userData.middleName : "",
      lastName: props.userData ? props.userData.lastName : "",
      dob: props.userData ? getFormattedMonthDateV2(props.userData.dob) : null,
      gender: props.userData ? props.userData.gender1 : "",
      userType: props.userData ? props.userData.userType1 : "",
      phoneNumber: props.userData ? props.userData.phoneNumber : "",
      email: props.userData ? props.userData.email : "",
      profilePic: null,
      previewImg: props.userData ? props.userData.profilePicOriginal : ""
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .min(2, "First name must be of minimum length 2")
        .max(25, "First name cannot be greater than 25 characters")
        .required("Please enter first name"),
      lastName: Yup.string()
        .min(2, "Last name must be of minimum length 2")
        .max(25, "Last name cannot be greater than 25 characters")
        .required("Please enter last name"),
      dob: Yup.mixed().required("Please select date of birth"),
      gender: Yup.string().required("Please select gender"),
      userType: Yup.string().required("Please select user type"),
      phoneNumber: Yup.string()
        .required("Please enter phone no")
        .matches(phoneRegex, "Please enter correct phone no"),
      email: Yup.string()
        .min(7, "Email must be of minimum length 7")
        .email("Please provide a valid email address")
        .required("Please enter email")
    }),
    onSubmit: values => {
      if (
        !props.isEditProfile &&
        (values.profilePic === null || fileError !== "")
      ) {
        setFileError("Please upload profile picture");
      } else {
        callStaffApi(values);
      }
    }
  });

  return (
    <Fragment>
      {isProcessing && <Loader />}
      <div className={isProcessing ? "disableContainer" : ""}>
        <form className="d-flex flex-column" onSubmit={formik.handleSubmit}>
          <Error>{serverErrorMsg}</Error>
          <div className="form-row mt-4">
            <div className="form-group col-md-12 d-flex flex-row">
              <label>
                Profile Pic <span className="error">*</span>
              </label>
              &nbsp;&nbsp;
              <img
                className="rounded-circle user-img ml-3 mr-4"
                src={
                  formik.values.previewImg !== ""
                    ? formik.values.previewImg
                    : ICTempImg
                }
                alt="user-img"
              />
              &nbsp;&nbsp;
              <p>
                <input
                  className="mb-2 pt-3"
                  type="file"
                  onChange={event => {
                    if (validateFile(event.currentTarget.files[0])) {
                      formik.setFieldValue(
                        "profilePic",
                        event.currentTarget.files[0]
                      );

                      formik.setFieldValue(
                        "previewImg",
                        URL.createObjectURL(event.currentTarget.files[0])
                      );
                    }
                  }}
                />
                <br />
                <span style={{ fontSize: "12px" }}>
                  JPG, SVG or PNG. Max size of 2MB
                </span>
                <br />
                <Error class="error">{fileError}</Error>
              </p>
            </div>
          </div>
          <div className="form-row mt-4">
            <div className="form-group col-md-4">
              <label>
                First Name <span className="error">*</span>
              </label>
              <ICInput
                name="firstName"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.firstName}
                error={
                  formik.touched.firstName && formik.errors.firstName
                    ? true
                    : false
                }
                helperText={
                  formik.touched.firstName && formik.errors.firstName
                    ? formik.errors.firstName
                    : ""
                }
              />
            </div>
            <div className="form-group col-md-4">
              <label>Middle Name</label>
              <ICInput
                name="middleName"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.middleName}
                error={
                  formik.touched.middleName && formik.errors.middleName
                    ? true
                    : false
                }
                helperText={
                  formik.touched.middleName && formik.errors.middleName
                    ? formik.errors.middleName
                    : ""
                }
              />
            </div>
            <div className="form-group col-md-4">
              <label>
                Last Name <span className="error">*</span>
              </label>
              <ICInput
                name="lastName"
                type="text"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.lastName}
                error={
                  formik.touched.lastName && formik.errors.lastName
                    ? true
                    : false
                }
                helperText={
                  formik.touched.lastName && formik.errors.lastName
                    ? formik.errors.lastName
                    : ""
                }
              />
            </div>
          </div>
          <div className="form-row mt-4">
            <div className="form-group col-md-4">
              <label>
                Date of Birth <span className="error">*</span>
              </label>
              <KeyboardDatePicker
                disableFuture={true}
                name="dob"
                autoOk
                variant="inline"
                mask="__-__-____"
                format="DD-MM-YYYY"
                value={formik.values.dob}
                onChange={date => {
                  formik.setFieldValue("dob", date);
                }}
                error={formik.touched.dob && formik.errors.dob ? true : false}
                helperText={
                  formik.touched.dob && formik.errors.dob
                    ? formik.errors.dob
                    : ""
                }
              />
            </div>
            <div className="form-group col-md-4">
              <label>
                Gender <span className="error">*</span>
              </label>
              <TextField
                name="gender"
                className="w-100"
                select
                value={formik.values.gender}
                SelectProps={{
                  native: true
                }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.gender && formik.errors.gender ? true : false
                }
                helperText={
                  formik.touched.gender && formik.errors.gender
                    ? formik.errors.gender
                    : ""
                }
              >
                <option>Select Gender</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
              </TextField>
            </div>
            <div className="form-group col-md-4">
              <label>
                Type <span className="error">*</span>
              </label>
              <TextField
                name="userType"
                className="w-100"
                select
                value={formik.values.userType}
                SelectProps={{
                  native: true
                }}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.userType && formik.errors.userType
                    ? true
                    : false
                }
                helperText={
                  formik.touched.userType && formik.errors.userType
                    ? formik.errors.userType
                    : ""
                }
              >
                <option>Select Type</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super User</option>
                <option value="SUPPORT_STAFF">Support Staff</option>
              </TextField>
            </div>
          </div>
          <div className="form-row mt-4">
            <div className="form-group col-md-6">
              <label>
                Phone Number <span className="error">*</span>
              </label>
              <ICPhoneInput
                name="phoneNumber"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phoneNumber}
                error={
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? true
                    : false
                }
                helperText={
                  formik.touched.phoneNumber && formik.errors.phoneNumber
                    ? formik.errors.phoneNumber
                    : ""
                }
                disabled={false}
              />
            </div>
            <div className="form-group col-md-6">
              <label>
                Email <span className="error">*</span>
              </label>
              <ICInput
                name="email"
                type="email"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
                error={
                  formik.touched.email && formik.errors.email ? true : false
                }
                helperText={
                  formik.touched.email && formik.errors.email
                    ? formik.errors.email
                    : ""
                }
              />
            </div>
          </div>
          <br />
          <p className="mb-0 d-flex justify-content-center">
            <Button title="Save" type="submit" />
            <Button
              className="insta-button-primary ml-3 mr-3 mb-4"
              title="Cancel"
              type="button"
              clickEvent={() => {
                formik.resetForm();
                props.cancelEvent();
              }}
            ></Button>
          </p>
          {/* API Call */}
          <MakeAPICall apiParam={apiCallParams} />
        </form>
      </div>
    </Fragment>
  );
};

export default AddLoginForm;

AddLoginForm.defaultProps = {
  isEditProfile: false,
  userData: null
};

AddLoginForm.propTypes = {
  cancelEvent: PropTypes.func.isRequired,
  isEditProfile: PropTypes.bool,
  userData: PropTypes.object,
  LoginAddedSuccess: PropTypes.func,
  LoginEditSuccess: PropTypes.func
};
